import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';
import { MoreVertical20Regular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonProps } from '../Button/Button';
import './OverflowToolbar.scss';

export interface OverflowToolbarAction {
  /** Unique identifier used by the overflow manager. */
  id: string;
  /** Button label, shown both in the toolbar and the overflow menu. Optional when `node` is provided. */
  label?: string;
  /** Leading icon, shown in the toolbar and the overflow menu item. */
  icon?: React.ReactElement;
  onClick?: () => void;
  disabled?: boolean;
  appearance?: ButtonProps['appearance'];
  /**
   * Higher priority means the action overflows later (stays visible longer).
   * Defaults to 0.
   */
  priority?: number;
  /**
   * Custom element rendered in the toolbar row instead of the default Button
   * built from `label`/`icon`. Use for split buttons, tooltip-wrapped triggers,
   * buttons with `loading`/`primaryTransparent`, etc.
   */
  node?: React.ReactNode;
  /**
   * Custom rendering inside the overflow menu. Defaults to a `MenuItem` built
   * from `label`/`icon`/`onClick`. Provide this (or at least a `label`) for any
   * action so it can still be reached once it overflows.
   */
  renderMenuItem?: () => React.ReactNode;
}

export interface OverflowToolbarProps {
  /** Collapsible action buttons. Overflowing actions move into the overflow menu. */
  actions: OverflowToolbarAction[];
  /**
   * Pinned content rendered on the leading edge. Never collapses (e.g. tabs,
   * a search field, category pills). Use this for content that does not belong
   * in a "more actions" menu.
   */
  startContent?: ReactNode;
  /**
   * Pinned content rendered on the trailing edge. Never collapses
   * (e.g. counters, badges, a reload button).
   */
  endContent?: ReactNode;
  className?: string;
  /** Accessible label for the overflow ("more actions") menu button. */
  overflowMenuAriaLabel?: string;
}

const renderActionNode = (action: OverflowToolbarAction): React.ReactNode => {
  if (action.node !== undefined) {
    return action.node;
  }

  return (
    <Button
      appearance={action.appearance}
      icon={action.icon}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
};

const OverflowMenuItem: React.FC<{ action: OverflowToolbarAction }> = ({ action }) => {
  const isVisible = useIsOverflowItemVisible(action.id);

  if (isVisible) {
    return null;
  }

  if (action.renderMenuItem) {
    return <>{action.renderMenuItem()}</>;
  }

  return (
    <MenuItem icon={action.icon} disabled={action.disabled} onClick={action.onClick}>
      {action.label}
    </MenuItem>
  );
};

const OverflowMenu: React.FC<{ actions: OverflowToolbarAction[]; ariaLabel: string }> = ({
  actions,
  ariaLabel,
}) => {
  const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={ref}
          appearance="transparent"
          icon={<MoreVertical20Regular />}
          aria-label={ariaLabel}
          className="overflowToolbar__menuButton"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {actions.map((action) => (
            <OverflowMenuItem key={action.id} action={action} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

/**
 * A responsive toolbar that collapses action buttons into a "more actions"
 * (three-dot) menu when there is not enough horizontal space, instead of
 * truncating their labels.
 *
 * Built on Fluent UI v9 overflow primitives, which measure the available width
 * via a `ResizeObserver` and toggle item visibility automatically.
 *
 * Supersedes the plain {@link Toolbar}:
 * - `actions` — collapsible items (default Button, or a custom `node`).
 * - `startContent` / `endContent` — pinned, never-collapsing content (tabs,
 *   search, badges, reload) that should not move into the overflow menu.
 *
 * @example
 * <OverflowToolbar
 *   startContent={<Tabs items={tabs} />}
 *   actions={[
 *     { id: 'add', label: 'Add', icon: <AddRegular />, appearance: 'primary', priority: 100, onClick: onAdd },
 *     { id: 'delete', label: 'Delete', icon: <DeleteRegular />, onClick: onDelete },
 *     { id: 'sync', node: <CustomSplitButton ... />, label: 'Sync', renderMenuItem: () => <MenuItem ...>Sync</MenuItem> },
 *   ]}
 *   endContent={<Badge>12 items</Badge>}
 * />
 */
export const OverflowToolbar: React.FC<OverflowToolbarProps> = ({
  actions,
  startContent,
  endContent,
  className,
  overflowMenuAriaLabel,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn('overflowToolbar', className)}>
      {startContent != null && <div className="overflowToolbar__start">{startContent}</div>}
      <Overflow minimumVisible={1}>
        <div className="overflowToolbar__actions">
          {actions.map((action) => (
            <OverflowItem key={action.id} id={action.id} priority={action.priority}>
              <div className="overflowToolbar__item">{renderActionNode(action)}</div>
            </OverflowItem>
          ))}
          <OverflowMenu
            actions={actions}
            ariaLabel={
              overflowMenuAriaLabel ??
              t('overflow_toolbar_more_actions', { defaultValue: 'Weitere Aktionen' })
            }
          />
        </div>
      </Overflow>
      {endContent != null && <div className="overflowToolbar__end">{endContent}</div>}
    </div>
  );
};

OverflowToolbar.displayName = 'OverflowToolbar';
